/**
 * SEOHead.jsx — per-route, bilingual meta tags for rpswiss.ch
 *
 * WHY THIS EXISTS
 * index.html gives every route on the site the same title, description and
 * canonical. That is a duplicate-content signal across your whole site and it
 * means no interior page (events, membership, a given article) can rank for
 * anything of its own. This component overrides the static tags per route.
 *
 * WHICH IMPLEMENTATION TO USE
 * - React 19+: you do not need Helmet at all. React hoists <title>, <meta>
 *   and <link> to <head> natively — delete the Helmet import and wrapper and
 *   return the tags in a fragment.
 * - React 18 or below: react-helmet-async, as written. (react-helmet itself
 *   is unmaintained and unsafe with concurrent rendering — use the -async fork.)
 *
 * IMPORTANT: this only helps human visitors and Google's second render wave.
 * If the crawler never executes your JS, none of these tags exist for it.
 * Pre-rendering remains the prerequisite, not the optional extra.
 */

import { Helmet } from 'react-helmet-async';

const ORIGIN = 'https://www.rpswiss.ch';
const DEFAULT_IMAGE = `${ORIGIN}/img/rp_swiss_small.png`;

/**
 * ROUTE COPY
 *
 * Keyed by a language-neutral route id. Note the deliberate asymmetry:
 *
 * RU titles chase broad Russian-language intent — relocation, work,
 * community. Low competition, high intent, yours to take.
 *
 * EN titles chase the narrow "Russian-speaking + Switzerland" intersection
 * ONLY. They do not use bare "professional network Switzerland" or
 * "networking Geneva" — InterNations, Glocals, AmCham and the chambers of
 * commerce own those and you will not displace them. The qualifier is the
 * entire competitive advantage; never drop it to sound broader.
 *
 * Keep titles ≤60 chars and descriptions ≤160 so they don't truncate.
 */

export const ROUTE_META = {
  home: {
    ru: {
      title: 'Ассоциация русскоязычных профессионалов в Швейцарии — RPSwiss',
      description:
        'RPSwiss — сообщество русскоязычных специалистов, руководителей и предпринимателей в Швейцарии. Нетворкинг, мероприятия, карьера и бизнес в Лозанне, Женеве и Цюрихе.',
    },
    en: {
      title: 'Russian-Speaking Professionals in Switzerland — RPSwiss',
      description:
        'RPSwiss connects Russian-speaking specialists, executives and founders working in Switzerland. Events and networking in Lausanne, Geneva and Zurich.',
    },
  },
  events: {
    ru: {
      title: 'Русскоязычное сообщество в Швейцарии: мероприятия и нетворкинг — RPSwiss',
      description:
        'Календарь встреч RPSwiss: профессиональный нетворкинг, лекции и дискуссии на русском языке в Лозанне, Женеве и Цюрихе. Расписание и регистрация.',
    },
    en: {
      title: 'Events for Russian-Speaking Professionals in Switzerland',
      description:
        'Upcoming RPSwiss networking evenings, talks and roundtables for Russian-speaking professionals across Lausanne, Geneva and Zurich. Dates and registration.',
    },
  },
  register: {
    ru: {
      title: 'Вступить в русскоязычное сообщество в Швейцарии — членство в RPSwiss',
      description:
        'Условия членства в Ассоциации русскоязычных профессионалов в Швейцарии: кто может вступить, что это даёт и как подать заявку.',
    },
    en: {
      title: 'Join RPSwiss — Membership for Professionals in Switzerland',
      description:
        'Who can join RPSwiss, what membership includes, and how to apply. For Russian-speaking professionals based in or moving to Switzerland.',
    },
  },
  about: {
    ru: {
      title: 'О нас — Русскоязычное сообщество и ассоциация в Швейцарии — RPSwiss',
      description:
        'История, цели и команда RPSwiss. Некоммерческая ассоциация в Лозанне, объединяющая русскоязычных специалистов в Швейцарии.',
    },
    en: {
      title: 'About RPSwiss — Association Based in Lausanne',
      description:
        'The story, mission and team behind RPSwiss, a non-profit association in Lausanne for Russian-speaking professionals in Switzerland.',
    },
  },
 register_professional: {
  "ru": {
    "title": "Каталог специалистов русскоязычного сообщества в Швейцарии — RPSwiss",
    "description": "Единый каталог русскоязычных специалистов, экспертов и бизнесов в Швейцарии. Найдите проверенных профессионалов в Лозанне, Женеве, Цюрихе и других кантонах."
  },
  "en": {
    "title": "Professional Directory — RPSwiss Association",
    "description": "Directory of Russian-speaking professionals, experts, and businesses in Switzerland. Find verified specialists across Lausanne, Geneva, Zurich, and all cantons."
  }
}

};

/**
 * Build the RU and EN URLs for a route.
 * Russian lives at the root because it is the primary audience language;
 * English lives under /en/. Trailing slashes are consistent on purpose —
 * /events and /events/ must not both resolve, or you have duplicates again.
 */
function buildUrls(slug) {
  const suffix = slug ? `${slug}/` : '';
  return {
    ru: `${ORIGIN}/${suffix}`,
    en: `${ORIGIN}/en/${suffix}`,
  };
}

export default function SEOHead({
  routeId = 'home',
  slug = '',          // '' for home, 'events', 'membership', 'about'
  lang = 'ru',        // 'ru' | 'en'
  title,              // optional override, e.g. an individual event name
  description,        // optional override
  image = DEFAULT_IMAGE,
  imageAlt,
  type = 'website',   // 'article' for posts, 'event' handled via JSON-LD
  noindex = false,    // true for thank-you pages, filtered views, drafts
  jsonLd,             // optional extra schema, e.g. an Event object
}) {
  const copy = ROUTE_META[routeId]?.[lang] ?? ROUTE_META.home[lang];
  const finalTitle = title ?? copy.title;
  const finalDescription = description ?? copy.description;

  const urls = buildUrls(slug);
  const canonical = urls[lang];
  const ogLocale = lang === 'ru' ? 'ru_RU' : 'en_US';
  const ogLocaleAlt = lang === 'ru' ? 'en_US' : 'ru_RU';

  return (
    <Helmet>
      {/* htmlAttributes must track the actual content language — Yandex and
          Bing lean on this more than Google does, and a page that declares
          lang="ru" while serving English is a contradictory signal. */}
      <html lang={lang} />

      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />

      {/* Canonical is per-route. This is the single most important line here. */}
      <link rel="canonical" href={canonical} />

      {/* hreflang must be emitted on BOTH language versions and must point at
          each other reciprocally. A one-directional hreflang is ignored. */}
      <link rel="alternate" hrefLang="ru" href={urls.ru} />
      <link rel="alternate" hrefLang="en" href={urls.en} />
      <link rel="alternate" hrefLang="x-default" href={urls.ru} />

      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, follow'
            : 'index, follow, max-snippet:-1, max-image-preview:large'
        }
      />

      {/* Open Graph — drives Telegram and LinkedIn previews, which for this
          audience are a bigger referral channel than Bing or Yandex. */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="RPSwiss" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt ?? finalTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

/**
 * USAGE
 *
 *   // Route component
 *   <SEOHead routeId="events" slug="events" lang={currentLang} />
 *
 *   // Individual event page — override copy, attach Event schema
 *   <SEOHead
 *     routeId="events"
 *     slug={`events/${event.slug}`}
 *     lang="ru"
 *     title={`${event.name} — RPSwiss`}
 *     description={event.summary}
 *     type="article"
 *     jsonLd={{
 *       '@context': 'https://schema.org',
 *       '@type': 'Event',
 *       name: event.name,
 *       startDate: event.startsAt,
 *       endDate: event.endsAt,
 *       eventStatus: 'https://schema.org/EventScheduled',
 *       eventAttendanceMode:
 *         'https://schema.org/OfflineEventAttendanceMode',
 *       inLanguage: 'ru',
 *       location: {
 *         '@type': 'Place',
 *         name: event.venueName,
 *         address: {
 *           '@type': 'PostalAddress',
 *           streetAddress: event.street,
 *           addressLocality: event.city,
 *           addressCountry: 'CH',
 *         },
 *       },
 *       organizer: { '@id': 'https://www.rpswiss.ch/#organization' },
 *     }}
 *   />
 *
 * ONE THING TO AVOID
 * Do not redirect visitors by IP or Accept-Language. Google explicitly warns
 * against it, and it breaks crawling: the bot arrives from a US IP, gets
 * bounced to /en/, and your Russian pages never get discovered. Offer a
 * visible, persistent language switch in the header instead — and when you
 * share the site in an English-language context, share the /en/ URL directly.
 */
